/**
 * waivers.js — requirement waivers stored on a case.
 *
 * A waiver records that a required document does not exist and why, so the
 * slot counts as satisfied ("No consent form — <reason> · by X · date").
 * Stored at `cases.type_specific.waivers`:
 *
 *   { [category]: { reason: string ≤500, byName, byEmail, at: ISO } }
 *
 * Only `consent_form` is waivable in this release (WAIVABLE_CATEGORIES, mirrors
 * api/src/lib/requirements.js). Spec: required_evidence_logic.md §3.
 *
 * Read-compat: PR #65 stored a bare `type_specific.no_consent_form === true`
 * for LHR Code 1–2 submissions. getWaivers() treats that flag as a consent_form
 * waiver with reason 'Declared at submission' (and no byName / byEmail / at — nobody stamped
 * it), the same way the server helper does, so both tiers agree on what the
 * legacy rows mean. The server drops the flag on write.
 */

export const WAIVABLE_CATEGORIES = ['consent_form'];
export const LEGACY_WAIVER_REASON = 'Declared at submission';

const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/**
 * Normalised waivers map for a case's type_specific bag. Always returns an
 * object (possibly empty). Each entry is { reason, byName, byEmail, at } —
 * legacy conversions also carry `legacy: true`.
 */
export function getWaivers(typeSpecific) {
  const ts = isObj(typeSpecific) ? typeSpecific : {};
  const out = {};
  const raw = isObj(ts.waivers) ? ts.waivers : {};
  for (const [category, w] of Object.entries(raw)) {
    if (!isObj(w)) continue;
    out[category] = {
      reason:  w.reason == null ? '' : String(w.reason),
      byName:  w.byName  ?? null,
      byEmail: w.byEmail ?? null,
      at:      w.at      ?? null,
    };
  }
  if (!out.consent_form && ts.no_consent_form === true) {
    out.consent_form = { reason: LEGACY_WAIVER_REASON, byName: null, byEmail: null, at: null, legacy: true };
  }
  return out;
}

/** The waiver for one category, or null. */
export function waiverFor(typeSpecific, category) {
  return getWaivers(typeSpecific)[category] || null;
}

/** True when `category` is waived on this case (legacy flag included). */
export function isWaived(typeSpecific, category) {
  return waiverFor(typeSpecific, category) != null;
}

/** True when `category` may be waived at all (this release: consent_form only). */
export function isWaivable(category) {
  return WAIVABLE_CATEGORIES.includes(category);
}
